import React, { useRef, useCallback, useContext, useState, forwardRef } from 'react';
import {
  entityService,
  entityTypeService,
  connectionService,
  customPropertyService,
  customPropertyListValueService,
  datasetService,
  datasetGroupService,
  datasourceService,
  fieldService,
  attributeService,
  attributeSensitivityService,
  attributeUnitService,
  datatypeService,
  glossaryService,
  scheduleService,
  statusService,
  systemService,
  userService,
  userGroupService,
} from '../services';
import { copyToClipboard, pluralize } from '../helpers';
import { useMessage, useSideDialog } from './';
import { ModalContext } from '../context/ModalContext';
import {
  Checkbox,
  CodeToolbar,
  TextareaCode,
  textareaCodeInitialState,
  editorStateToPlainText,
} from '../components';

const objectMap = {
  entity_count: 'Entity',
  entity_type_count: 'Entity Type',
  connection_count: 'Connection',
  custom_property_count: 'Custom Property',
  custom_property_value_count: 'Custom Property Value',
  custom_property_list_value_count: 'Custom Property List Value',
  dataset_count: 'Dataset',
  dataset_group_count: 'Dataset Group',
  datasource_count: 'Datasource',
  datatype_count: 'Datatype',
  driver_count: 'Driver',
  oidc_client_count: 'Client',
  field_count: 'Field',
  attribute_count: 'Attribute',
  attribute_value_count: 'Attribute Value',
  attribute_sensitivity_count: 'Data Sensitivity Class',
  attribute_unit_count: 'Attribute Unit',
  glossary_count: 'Glossary',
  schedule_count: 'Schedule',
  status_count: 'Status',
  system_count: 'System',
  task_count: 'Task',
  user_count: 'User',
  user_group_count: 'User Group',
};

/** What happens to an asset that is being deleted. The default effect for any count with no override. */
const willBeDeleted = () => 'will be deleted';

/**
 * What happens to a Field whose Attribute goes away: the field itself stays in Katalogue, it just
 * stops inheriting a description from the attribute that described it. Shared by every delete that
 * unlinks fields — an attribute, its glossary, or removing the association by hand.
 */
const willLoseDescriptions = count =>
  count > 1 ? 'will lose their descriptions' : 'will lose its description';

/**
 * What happens to a Custom Property Value when the property or list value it belongs to is deleted: the
 * value disappears from every asset that carried it.
 */
const willBeDeletedFromAssets = () => 'will be deleted from their assets';

/**
 * One count of a delete confirmation: "12 Attributes".
 *
 * @param {string} key - A `*_count` key, naming the label in objectMap.
 * @param {number} count - How many.
 * @returns {string}
 */
const formatCheckDeleteCount = (key, count) => `${count} ${pluralize(objectMap[key], count)}`;

/**
 * One line of a delete confirmation that states an effect: "216 Fields will lose their descriptions".
 *
 * The only place this sentence is built.
 *
 * @param {string} key - A `*_count` key, naming the label in objectMap.
 * @param {number} count - How many.
 * @param {(count: number) => string} [effect] - What happens to them. Defaults to "will be deleted".
 * @returns {string} The finished sentence.
 */
const formatCheckDeleteChange = (key, count, effect = willBeDeleted) =>
  `${formatCheckDeleteCount(key, count)} ${effect(count)}`;

/**
 * The body of a delete confirmation dialog where everything listed is simply deleted: a heading and the
 * counts, with no per-line verb to repeat.
 *
 * @param {{ result: Record<string, number> }} checkDelete - A check_delete response.
 */
const renderCheckDelete = checkDelete => {
  return (
    <div>
      <div>Are you sure you want to delete the following assets?</div>
      <ul>
        {Object.entries(checkDelete.result)
          .filter(([, count]) => count > 0)
          .map(([key, count]) => (
            <li key={key}>{formatCheckDeleteCount(key, count)}</li>
          ))}
      </ul>
    </div>
  );
};

/**
 * The body of a delete confirmation dialog where the delete does more than delete: one sentence per kind
 * of asset, each stating its own effect.
 *
 * Used only where a bare list of counts would be misleading — deleting an attribute or its glossary
 * leaves the fields they described in place and merely strips the description those fields inherited,
 * and removing a field association deletes nothing at all. Anything that only deletes should use
 * renderCheckDelete instead, so the wording stays as short as the operation.
 *
 * Lines read in the order the API returned the counts, so the response decides what comes first —
 * outermost asset first, and anything that survives the delete last.
 *
 * @param {{ result: Record<string, number> }} checkDelete - A check_delete response.
 * @param {Record<string, (count: number) => string>} [effects] - Per-key effect overrides, for
 *   anything that is not simply deleted.
 */
const renderCheckDeleteChanges = (checkDelete, effects = {}) => {
  return (
    <div>
      <div>Are you sure you want to proceed with the following changes?</div>
      <ul>
        {Object.entries(checkDelete.result)
          .filter(([, count]) => count > 0)
          .map(([key, count]) => (
            <li key={key}>{formatCheckDeleteChange(key, count, effects[key])}</li>
          ))}
      </ul>
    </div>
  );
};

const _QueryEditor = ({ data, onSubmit, loading, readOnly = false, language = 'sql' }, ref) => {
  const [value, setValue] = useState(textareaCodeInitialState({ value: data }));

  const handleInputChange = event => {
    setValue(event.value);
  };

  const handleSubmit = event => {
    event.preventDefault();
    onSubmit(editorStateToPlainText(value));
  };

  return (
    <div className="modal-container-body-code">
      <form ref={ref} onSubmit={handleSubmit}>
        {/* The same row the technical definition panel uses: what this is on the left, copy on the
            right. These queries are always SQL, so there is nothing to choose between. */}
        <CodeToolbar language={language} copyText={editorStateToPlainText(value)} />
        <TextareaCode
          name="code-editor"
          value={value}
          onChange={handleInputChange}
          placeholder={language === 'sql' ? 'SELECT * FROM schema.vw_custom_view' : undefined}
          disabled={loading}
          readOnly={readOnly}
          language={language}
        />
      </form>
    </div>
  );
};
const QueryEditor = forwardRef(_QueryEditor);

/**
 * An interactive component that renders content for dialog to delete assets that can be nested.
 * @param {*} param0
 * @param {*} ref
 * @returns
 */
const _DeleteBody = ({ data, onSubmit, loading }, ref) => {
  const { items, checkDelete } = data;

  const [deleteChildren, setDeleteChildren] = useState(false);

  const handleChange = () => {
    setDeleteChildren(!deleteChildren);
  };

  const handleSubmit = event => {
    event.preventDefault();
    onSubmit({
      items,
      deleteChildren,
    });
  };

  // An asset can have children of more than one kind — an entity holds entities and attributes — and
  // they are all kept or all deleted together, so they are described in one checkbox rather than one
  // per kind, which would look like several independent choices.
  const children = Object.entries(checkDelete.result['children'] || {}).filter(
    ([, count]) => count > 0
  );
  const childCounts = children.map(([key, count]) => formatCheckDeleteCount(key, count));
  // Always plural in the tooltip: it describes the rule, not this particular selection.
  const childKinds = children.map(([key]) => pluralize(objectMap[key], 2));

  return (
    <div>
      <div>Are you sure you want to delete the following assets?</div>
      <form ref={ref} onSubmit={handleSubmit}>
        <ul>
          {/* The list describes the asset itself and what goes with it regardless of the choice
              below, so the counts stay as the server reported them and do not move when the
              checkbox is ticked. Folding the children into these counts would also hide a whole
              kind: a list line only appears for a kind that has a count of its own, so an entity
              whose only children are attributes showed a rising entity count and no attributes at
              all. The children are named in the checkbox label instead. */}
          {Object.keys(checkDelete.result).map(key =>
            key !== 'children' && checkDelete.result[key] > 0 ? (
              <li key={key}>{formatCheckDeleteCount(key, checkDelete.result[key])}</li>
            ) : null
          )}
        </ul>
        {childCounts.length > 0 && (
          <ul>
            <li>
              <Checkbox
                checked={deleteChildren}
                name="delete_children"
                label={`Also delete all children: ${childCounts.join(' and ')}`}
                onChange={handleChange}
                disabled={loading}
                title={`If checked, all child ${childKinds.join(' and ')} will also be deleted.\nIf not checked, the children will be lifted up one level.`}
              />
            </li>
          </ul>
        )}
      </form>
    </div>
  );
};

const DeleteBody = forwardRef(_DeleteBody);

export const useModalDialog = () => {
  const { modalDialog, setModalDialog, loadingModalDialog, setLoadingModalDialog, show, setShow } =
    useContext(ModalContext);

  const refContainerBody = useRef();
  const refContentForm = useRef();

  const { showMessage } = useMessage();
  const { hideSideDialog } = useSideDialog();

  const checkDeleteEntity = async items => {
    return entityService
      .checkDestroy(items)
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        hideModalDialog();
      });
  };

  const checkDeleteEntityType = async items => {
    return entityTypeService
      .checkDestroy(items)
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        hideModalDialog();
      });
  };

  const checkDeleteCustomProperty = async items => {
    return customPropertyService
      .checkDestroy(items)
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        hideModalDialog();
      });
  };

  const checkDeleteCustomPropertyListValue = async items => {
    return customPropertyListValueService
      .checkDestroy(items)
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        hideModalDialog();
      });
  };

  const checkDeleteDataset = async items => {
    return datasetService
      .checkDestroy(items)
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        hideModalDialog();
      });
  };

  const checkDeleteDatasetGroup = async items => {
    return datasetGroupService
      .checkDestroy(items)
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        hideModalDialog();
      });
  };

  const checkDeleteDatasource = async items => {
    return datasourceService
      .checkDestroy(items)
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        hideModalDialog();
      });
  };

  const checkDeleteField = async items => {
    return fieldService
      .checkDestroy(items)
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        hideModalDialog();
      });
  };

  const checkDeleteAttribute = async items => {
    return attributeService
      .checkDestroy(items)
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        hideModalDialog();
      });
  };

  const checkDeleteAttributeSensitivity = async items => {
    return attributeSensitivityService
      .checkDestroy(items)
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        hideModalDialog();
      });
  };

  const checkDeleteAttributeUnit = async items => {
    return attributeUnitService
      .checkDestroy(items)
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        hideModalDialog();
      });
  };

  const checkDeleteDatatype = async items => {
    return datatypeService
      .checkDestroy(items)
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        hideModalDialog();
      });
  };

  const checkDeleteGlossary = async items => {
    return glossaryService
      .checkDestroy(items)
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        hideModalDialog();
      });
  };

  const checkDeleteSchedule = async items => {
    return scheduleService
      .checkDestroy(items)
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        hideModalDialog();
      });
  };

  const checkDeleteStatus = async items => {
    return statusService
      .checkDestroy(items)
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        hideModalDialog();
      });
  };

  const checkDeleteSystem = async items => {
    return systemService
      .checkDestroy(items)
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        hideModalDialog();
      });
  };

  const checkDeleteConnection = async items => {
    return connectionService
      .checkDestroy(items)
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        hideModalDialog();
      });
  };

  const checkDeleteUser = async items => {
    return userService
      .checkDestroy(items)
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        hideModalDialog();
      });
  };

  const checkDeleteUserGroup = async items => {
    return userGroupService
      .checkDestroy(items)
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        hideModalDialog();
      });
  };

  const getRelationshipImportQuery = async items => {
    return connectionService
      .getRelationshipImportQuery(items.connection_id, {
        relationship_map: items.relationship_map,
        virtual_relationships_is_enabled: items.virtual_relationships_is_enabled,
      })
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        return { query: 'Error when compiling query' };
      });
  };

  const validateDatasourceFilter = async items => {
    return connectionService
      .validateDatasourceFilter({ connection: items })
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
        return { query: 'Error when compiling query' };
      });
  };

  const getDefaultImportQuery = async items => {
    return connectionService
      .getDefaultImportQuery({ connection: items })
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const getDefaultRelationshipQuery = async items => {
    return connectionService
      .getDefaultRelationshipQuery({ connection: items })
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const getDefaultLineageQuery = async items => {
    return connectionService
      .getDefaultLineageQuery({ connection: items })
      .then(async res => {
        return res;
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const hideModalDialog = useCallback(() => {
    setShow(false);
    setModalDialog({});

    // Hacky way to un-blur side dialog.
    // That component cannot use this hook to get the "show" value since that causes a re-render and I found no other way to prevent that
    // David 2023-01-17
    document.getElementById('SideDialog').classList.remove('filter-blur');
  }, [setShow]);

  const showModalDialog = useCallback(
    async (mode, data, onSubmit, onCancel, submitLabel, submitLabelLoading, cancelLabel) => {
      setLoadingModalDialog(true);

      let defaultConfig = {
        active: true,
        title: 'You are about to delete some stuff',
        submitLabel: submitLabel !== undefined ? submitLabel : 'OK',
        submitLabelLoading: submitLabelLoading ? submitLabelLoading : submitLabel || 'Deleting...',
        cancelLabel: cancelLabel !== undefined ? cancelLabel : 'Cancel',
        onSubmit: () => (onSubmit ? onSubmit(data) : hideModalDialog()),
        onCancel: onCancel || hideModalDialog,
        customButtons: undefined,
        Content: undefined,
        contentProps: undefined,
      };

      let config = {};
      let checkDelete = {};

      switch (mode) {
        case 'removeFieldAssociation':
          hideSideDialog();
          config = {
            ...config,
            title: 'You are about to remove some field associations',
            submitLabelLoading: 'Removing...',
          };
          break;

        case 'removeMember':
          hideSideDialog();
          config = {
            ...config,
            title: 'You are about to remove some users from a user group',
            submitLabelLoading: 'Removing...',
          };
          break;

        case 'removeCustomPropertyValue':
          hideSideDialog();
          config = {
            ...config,
            title: 'Delete Custom Property Values',
            submitLabelLoading: 'Deleting...',
          };
          break;

        case 'removeTaskFromSchedule':
          hideSideDialog();
          config = {
            ...config,
            title: 'You are about to remove tasks from this schedule',
            submitLabelLoading: 'Removing...',
          };
          break;

        case 'removeScheduleFromTask':
          hideSideDialog();
          config = {
            ...config,
            title: 'You are about to remove this task from schedules',
            submitLabelLoading: 'Removing...',
          };
          break;

        case 'showRelationshipQuery':
          config = {
            ...config,
            title: 'Relationship Upsert Query',
            cancelLabel: undefined,
            submitLabel: 'Close',
            submitLabelLoading: 'OK',
          };
          break;

        case 'validateDatasourceFilter':
          config = {
            ...config,
            title: 'Datasource Filter',
            cancelLabel: undefined,
            submitLabel: 'Close',
            submitLabelLoading: 'OK',
          };
          break;

        // Any code field that wants room to work in. The query cases below are the same thing with
        // a fixed title and a "default query" button; this one takes its title, language and
        // read-only state from the caller, so a TextareaCode anywhere can hand its value over and
        // take the edited text back.
        case 'editCode':
          config = {
            ...config,
            title: data?.title || 'Edit',
            cancelLabel: 'Cancel',
            submitLabel: data?.readOnly ? undefined : 'Save',
            submitLabelLoading: 'Saving...',
            contentProps: {
              data: data?.value,
              onSubmit: onSubmit,
              readOnly: data?.readOnly,
              language: data?.language,
              ref: refContentForm,
            },
            onSubmit: () =>
              refContentForm.current.dispatchEvent(
                new Event('submit', { cancelable: true, bubbles: true })
              ),
          };
          break;

        case 'editImportQuery':
          config = {
            ...config,
            title: 'Edit Schema Query',
            cancelLabel: 'Cancel',
            submitLabel: 'Save',
            submitLabelLoading: 'Saving...',
            customButtons: [
              {
                value: 'Insert Default Query',
                valueLoading: 'Inserting Default Query...',
                tooltip: 'Insert default SQL query. This will overwrite the current query above',
                onClick: async () => {
                  const defaultQuery = await getDefaultImportQuery(data);
                  const tmp = {
                    ...data,
                    custom_import_query: defaultQuery.query,
                  };
                  showModalDialog(
                    'editImportQuery',
                    tmp,
                    onSubmit,
                    onCancel,
                    submitLabel,
                    submitLabelLoading,
                    cancelLabel
                  );
                },
              },
            ],
            contentProps: {
              data: data.custom_import_query,
              onSubmit: onSubmit,
              ref: refContentForm,
            },
            onSubmit: () =>
              refContentForm.current.dispatchEvent(
                new Event('submit', { cancelable: true, bubbles: true })
              ),
          };
          break;

        case 'editRelationshipQuery':
          config = {
            ...config,
            title: 'Edit Relationship Query',
            cancelLabel: 'Cancel',
            submitLabel: 'Save',
            submitLabelLoading: 'Saving...',
            customButtons: [
              {
                value: 'Insert Default Query',
                valueLoading: 'Inserting Default Query...',
                tooltip: 'Insert default SQL query. This will overwrite the current query above',
                onClick: async () => {
                  const defaultQuery = await getDefaultRelationshipQuery(data);
                  const tmp = {
                    ...data,
                    custom_relationship_query: defaultQuery.query,
                  };
                  showModalDialog(
                    'editRelationshipQuery',
                    tmp,
                    onSubmit,
                    onCancel,
                    submitLabel,
                    submitLabelLoading,
                    cancelLabel
                  );
                },
              },
            ],
            contentProps: {
              data: data.custom_relationship_query,
              onSubmit: onSubmit,
              ref: refContentForm,
            },
            onSubmit: () =>
              refContentForm.current.dispatchEvent(
                new Event('submit', { cancelable: true, bubbles: true })
              ),
          };
          break;

        case 'editLineageQuery':
          config = {
            ...config,
            title: 'Edit Lineage Query',
            cancelLabel: 'Cancel',
            submitLabel: 'Save',
            submitLabelLoading: 'Saving...',
            customButtons: [
              {
                value: 'Insert Default Query',
                valueLoading: 'Inserting Default Query...',
                tooltip: 'Insert default SQL query. This will overwrite the current query above',
                onClick: async () => {
                  const defaultQuery = await getDefaultLineageQuery(data);
                  const tmp = {
                    ...data,
                    custom_lineage_query: defaultQuery.query,
                  };
                  showModalDialog(
                    'editLineageQuery',
                    tmp,
                    onSubmit,
                    onCancel,
                    submitLabel,
                    submitLabelLoading,
                    cancelLabel
                  );
                },
              },
            ],
            contentProps: {
              data: data.custom_lineage_query,
              onSubmit: onSubmit,
              ref: refContentForm,
            },
            onSubmit: () =>
              refContentForm.current.dispatchEvent(
                new Event('submit', { cancelable: true, bubbles: true })
              ),
          };
          break;

        case 'showMessage':
          config = {
            ...config,
            title: 'Job Completion Message',
            cancelLabel: undefined,
            submitLabel: 'Close',
            customButtons: [
              {
                value: 'Copy',
                valueClicked: 'Copied!',
                tooltip: 'Copy to clipboard',
                onClick: () => {
                  copyToClipboard(refContainerBody);
                },
              },
            ],
            submitLabelLoading: 'OK',
          };
          break;

        case 'editSettings':
          config = {
            ...config,
            title: 'Saving settings will trigger a reboot',
            submitLabel: 'Save',
            submitLabelLoading: 'OK',
          };
          break;

        case 'generateClientSecret':
          config = {
            ...config,
            title: `Generate New Secret For ${data.length} Clients`,
            submitLabel: 'Generate',
            submitLabelLoading: 'Generating...',
          };
          break;

        case 'rotateOidcSigningKey':
          config = {
            ...config,
            title: `Rotate Signing Key`,
            submitLabel: 'OK',
            submitLabelLoading: 'OK',
          };
          break;

        case 'addedOidcClient':
          config = {
            ...config,
            title: `Client Secret`,
            submitLabel: 'OK',
            cancelLabel: undefined,
          };
          break;

        default:
          hideSideDialog();
          break;
      }

      setShow(true);
      setModalDialog({ ...defaultConfig, ...config });

      // David Svensson 2023-01-17
      // Hacky way to blur side dialog.
      // That component cannot use this hook to get the "show" value since that causes a re-render and I found no other way to prevent that
      document.getElementById('SideDialog').classList.add('filter-blur');

      switch (mode) {
        case 'deleteEntity':
          // Note: This mode is triggered either from Glossary.js or Entity.js

          checkDelete = await checkDeleteEntity({ entities: data });

          config = {
            ...config,
            contentProps: {
              data: { items: data, checkDelete },
              onSubmit: onSubmit,
              ref: refContentForm,
            },
            Content: DeleteBody,
            onSubmit: () =>
              refContentForm.current.dispatchEvent(
                new Event('submit', { cancelable: true, bubbles: true })
              ),
          };
          break;
        case 'deleteEntityType':
          checkDelete = await checkDeleteEntityType({ entity_types: data });
          config = {
            ...config,
            Content: () => renderCheckDelete(checkDelete),
          };
          break;
        case 'deleteConnection':
          checkDelete = await checkDeleteConnection({ connections: data });
          config = {
            ...config,
            Content: () => renderCheckDelete(checkDelete),
          };
          break;
        case 'deleteCustomProperty':
          checkDelete = await checkDeleteCustomProperty({
            custom_properties: data,
          });
          config = {
            ...config,
            Content: () =>
              renderCheckDeleteChanges(checkDelete, {
                custom_property_value_count: willBeDeletedFromAssets,
              }),
          };
          break;
        case 'deleteCustomPropertyListValue':
          checkDelete = await checkDeleteCustomPropertyListValue({
            custom_property_list_values: data,
          });
          config = {
            ...config,
            Content: () =>
              renderCheckDeleteChanges(checkDelete, {
                custom_property_value_count: willBeDeletedFromAssets,
              }),
          };
          break;
        case 'deleteDataset':
          checkDelete = await checkDeleteDataset({ datasets: data });
          config = {
            ...config,
            Content: () => renderCheckDelete(checkDelete),
          };
          break;
        case 'deleteDatasetGroup':
          checkDelete = await checkDeleteDatasetGroup({ dataset_groups: data });
          config = {
            ...config,
            Content: () => renderCheckDelete(checkDelete),
          };
          break;
        case 'deleteDatasource':
          checkDelete = await checkDeleteDatasource({ datasources: data });
          config = {
            ...config,
            Content: () => renderCheckDelete(checkDelete),
          };
          break;
        case 'deleteDatatype':
          checkDelete = await checkDeleteDatatype({ datatypes: data });

          if (checkDelete.result.length > 0) {
            config = {
              ...config,
              title: `Cannot delete datatype${data.length > 1 ? 's' : ''}`,
              submitLabel: 'OK',
              cancelLabel: undefined,
              onSubmit: hideModalDialog,
              Content: () => (
                <div>
                  <p>There are fields using the datatype you are trying to delete.</p>
                  <p>Re-assign the fields to other datatypes to be able to delete these.</p>
                  <p>Assets with {data.length > 1 ? 'these datatypes' : 'this datatype'}:</p>
                  <ul>
                    {checkDelete.result.map((x, index) => (
                      <li key={index}>
                        {`${x.asset_count} ${pluralize(objectMap[x.asset_type + '_count'], x.asset_count)}`}
                      </li>
                    ))}
                  </ul>
                </div>
              ),
            };
          } else {
            config = {
              ...config,
              Content: () => renderCheckDelete({ result: { datatype_count: data.length } }),
            };
          }
          break;
        case 'deleteODBCDriver':
          config = {
            ...config,
            Content: () => renderCheckDelete({ result: { driver_count: data.length } }),
          };
          break;
        case 'deleteOidcClient':
          config = {
            ...config,
            Content: () =>
              renderCheckDelete({
                result: { oidc_client_count: data.length },
              }),
          };
          break;
        case 'deleteField':
          checkDelete = await checkDeleteField({ fields: data });

          config = {
            ...config,
            contentProps: {
              data: { items: data, checkDelete },
              onSubmit: onSubmit,
              ref: refContentForm,
            },
            Content: DeleteBody,
            onSubmit: () =>
              refContentForm.current.dispatchEvent(
                new Event('submit', { cancelable: true, bubbles: true })
              ),
          };

          //config = {
          //  ...config,
          //  Content: () => renderCheckDelete(checkDelete),
          //};
          break;
        case 'deleteAttributeValue':
          config = {
            ...config,
            Content: () =>
              renderCheckDelete({
                result: { attribute_value_count: data.length },
              }),
          };
          break;
        case 'deleteAttribute':
          checkDelete = await checkDeleteAttribute({
            attributes: data,
          });
          config = {
            ...config,
            Content: () =>
              renderCheckDeleteChanges(checkDelete, { field_count: willLoseDescriptions }),
          };
          break;
        case 'removeFieldAssociation':
          config = {
            ...config,
            Content: () =>
              renderCheckDeleteChanges(
                { result: { field_count: data.length } },
                { field_count: willLoseDescriptions }
              ),
          };
          break;
        case 'deleteAttributeSensitivity':
          checkDelete = await checkDeleteAttributeSensitivity({ attribute_sensitivities: data });

          if (checkDelete.result.length > 0) {
            config = {
              ...config,
              title: `Cannot delete data sensitivity class${data.length > 1 ? 'es' : ''}`,
              submitLabel: 'OK',
              cancelLabel: undefined,
              onSubmit: hideModalDialog,
              Content: () => (
                <div>
                  <p>
                    There are assets assigned to the data sensitivity class you are trying to
                    delete.
                  </p>
                  <p>
                    Re-assign the assets to other data sensitivity classes to be able to delete
                    these.
                  </p>
                  <p>
                    Assets with{' '}
                    {data.length > 1
                      ? 'these data sensitivity classes'
                      : 'this data sensitivity class'}
                    :
                  </p>
                  <ul>
                    {checkDelete.result.map((x, index) => (
                      <li key={index}>
                        {`${x.asset_count} ${pluralize(objectMap[x.asset_type + '_count'], x.asset_count)}`}
                      </li>
                    ))}
                  </ul>
                </div>
              ),
            };
          } else {
            config = {
              ...config,
              Content: () =>
                renderCheckDelete({ result: { attribute_sensitivity_count: data.length } }),
            };
          }
          break;
        case 'deleteAttributeUnit':
          checkDelete = await checkDeleteAttributeUnit({ attribute_units: data });

          if (checkDelete.result.length > 0) {
            config = {
              ...config,
              title: `Cannot delete attribute unit${data.length > 1 ? 's' : ''}`,
              submitLabel: 'OK',
              cancelLabel: undefined,
              onSubmit: hideModalDialog,
              Content: () => (
                <div>
                  <p>There are attributes using the attribute unit you are trying to delete.</p>
                  <p>
                    Re-assign the attributes to other attribute units to be able to delete these.
                  </p>
                  <p>
                    Assets with {data.length > 1 ? 'these attribute units' : 'this attribute unit'}:
                  </p>
                  <ul>
                    {checkDelete.result.map((x, index) => (
                      <li key={index}>
                        {`${x.asset_count} ${pluralize(objectMap[x.asset_type + '_count'], x.asset_count)}`}
                      </li>
                    ))}
                  </ul>
                </div>
              ),
            };
          } else {
            config = {
              ...config,
              Content: () => renderCheckDelete({ result: { attribute_unit_count: data.length } }),
            };
          }
          break;
        case 'deleteGlossary':
          checkDelete = await checkDeleteGlossary({ glossaries: data });
          config = {
            ...config,
            Content: () =>
              renderCheckDeleteChanges(checkDelete, { field_count: willLoseDescriptions }),
          };
          break;
        case 'deleteStatus':
          checkDelete = await checkDeleteStatus({ statuses: data });

          if (checkDelete.result.length > 0) {
            config = {
              ...config,
              title: `Cannot delete status${data.length > 1 ? 'es' : ''}`,
              submitLabel: 'OK',
              cancelLabel: undefined,
              onSubmit: hideModalDialog,
              Content: () => (
                <div>
                  <p>There are assets assigned to the statuses you are trying to delete.</p>
                  <p>Re-assign the assets to other statuses to be able to delete these.</p>
                  <p>Assets with {data.length > 1 ? 'these statuses' : 'this status'}:</p>
                  <ul>
                    {checkDelete.result.map((x, index) => (
                      <li key={index}>
                        {`${x.asset_count} ${pluralize(objectMap[x.asset_type + '_count'], x.asset_count)}`}
                      </li>
                    ))}
                  </ul>
                </div>
              ),
            };
          } else {
            config = {
              ...config,
              Content: () => renderCheckDelete({ result: { status_count: data.length } }),
            };
          }
          break;
        case 'deleteSystem':
          checkDelete = await checkDeleteSystem({ systems: data });
          config = {
            ...config,
            Content: () => renderCheckDelete(checkDelete),
          };
          break;
        case 'deleteSchedule':
          checkDelete = await checkDeleteSchedule({ schedules: data });
          config = {
            ...config,
            Content: () => (
              <div>
                <div>Are you sure you want to proceed with the following changes?</div>
                <ul>
                  <li>
                    {data.length} Schedule{data.length > 1 ? 's' : ''} will be deleted
                  </li>
                  {checkDelete?.result?.task_count > 0 && (
                    <li>
                      {checkDelete.result.task_count} Task
                      {checkDelete.result.task_count > 1 ? 's' : ''} will lose their schedule
                      assignment
                    </li>
                  )}
                </ul>
              </div>
            ),
          };
          break;
        case 'deleteTask':
          config = {
            ...config,
            Content: () => (
              <div>
                <div>Are you sure you want to proceed with the following changes?</div>
                <ul>
                  {data.length > 0 && (
                    <li>
                      {data.length + ' Task' + (data.length > 1 ? 's' : '')} and related Job logs
                      will be deleted
                    </li>
                  )}
                </ul>
              </div>
            ),
          };
          break;
        case 'deleteUser':
          checkDelete = await checkDeleteUser({ users: data });

          if (checkDelete) {
            config = {
              ...config,
              Content: () => (
                <div>
                  <div>Are you sure you want to proceed with the following changes?</div>
                  <ul>
                    {checkDelete.result.user_delete_count > 0 && (
                      <li>
                        {checkDelete.result.user_delete_count +
                          ' User' +
                          (checkDelete.result.user_delete_count > 1 ? 's' : '')}{' '}
                        will be deleted
                      </li>
                    )}
                    {checkDelete.result.user_disable_count > 0 && (
                      <li>
                        {checkDelete.result.user_disable_count +
                          ' User' +
                          (checkDelete.result.user_disable_count > 1
                            ? 's have assets assigned to them and will be disabled'
                            : ' has assigned assets and will be disabled')}
                      </li>
                    )}
                  </ul>
                </div>
              ),
            };
          } else {
            hideModalDialog();
          }
          break;
        case 'deleteUserGroup':
          checkDelete = await checkDeleteUserGroup({ user_groups: data });
          /*config = {
            ...config,
            Content: () => renderCheckDelete(checkDelete),
          };*/
          if (checkDelete) {
            config = {
              ...config,
              Content: () => (
                <div>
                  <div>Are you sure you want to proceed with the following changes?</div>
                  <ul>
                    {checkDelete.result.user_group_delete_count > 0 && (
                      <li>
                        {checkDelete.result.user_group_delete_count +
                          ' User Group' +
                          (checkDelete.result.user_group_delete_count > 1 ? 's' : '')}{' '}
                        will be deleted
                      </li>
                    )}
                    {checkDelete.result.user_group_skip_count > 0 && (
                      <li>
                        {checkDelete.result.user_group_skip_count +
                          ' User Group' +
                          (checkDelete.result.user_group_skip_count > 1
                            ? 's have assets assigned to them and can therefore not be deleted'
                            : ' has assigned assets and can therefore not be deleted')}
                      </li>
                    )}
                  </ul>
                </div>
              ),
            };
          } else {
            hideModalDialog();
          }
          break;
        case 'removeMember':
          config = {
            ...config,
            Content: () => (
              <div>
                <div>Are you sure you want to proceed with the following changes?</div>
                <ul>{data.length > 0 && <li>{data.length + ' Users will be removed'} </li>}</ul>
              </div>
            ),
          };
          break;
        case 'removeCustomPropertyValue':
          config = {
            ...config,
            Content: () => (
              <div>
                <div>Are you sure you want to proceed with the following changes?</div>
                <ul>
                  {data.length > 0 && (
                    <li>
                      {data.length +
                        ' Custom Property Values will be deleted from their assets'}{' '}
                    </li>
                  )}
                </ul>
              </div>
            ),
          };
          break;
        case 'removeUserGroup':
          config = {
            ...config,
            Content: () => (
              <div>
                <div>Are you sure you want to proceed with the following changes?</div>
                <ul>
                  {data.length > 0 && (
                    <li>{'User will be removed from ' + data.length + ' User Groups'} </li>
                  )}
                </ul>
              </div>
            ),
          };
          break;
        case 'removeTaskFromSchedule':
          config = {
            ...config,
            Content: () => (
              <div>
                <div>Are you sure you want to proceed with the following changes?</div>
                <ul>
                  {data.length > 0 && (
                    <li>
                      {data.length +
                        (data.length === 1 ? ' Task' : ' Tasks') +
                        ' will be removed from this schedule'}
                    </li>
                  )}
                </ul>
              </div>
            ),
          };
          break;
        case 'removeScheduleFromTask':
          config = {
            ...config,
            Content: () => (
              <div>
                <div>Are you sure you want to proceed with the following changes?</div>
                <ul>
                  {data.length > 0 && (
                    <li>
                      {data.length +
                        (data.length === 1 ? ' Schedule' : ' Schedules') +
                        ' will be removed from this task'}
                    </li>
                  )}
                </ul>
              </div>
            ),
          };
          break;
        case 'showRelationshipQuery':
          const relationshipQuery = await getRelationshipImportQuery(data);
          config = {
            ...config,
            /* This is now part of the editor toolbar header.
            customButtons: [
              {
                value: 'Copy',
                valueClicked: 'Copied!',
                tooltip: 'Copy query to clipboard',
                onClick: () => {
                  copyToClipboard(relationshipQuery.query);
                },
              },
            ],*/
            contentProps: {
              data: relationshipQuery.query,
              ref: refContentForm,
              readOnly: true,
            },
            Content: QueryEditor,
          };
          break;
        case 'validateDatasourceFilter': {
          // The form knows whether this connector uses the shared in-memory filter and passes it in
          // (UI-only — stripped here so it isn't sent to the backend). Common-filter connectors get
          // a validation message rendered as plain text; database connectors get the generated SQL
          // in the read-only code editor with a copy button.
          const { useCommonDatasourceFilter, ...connection } = data;
          const filterQuery = await validateDatasourceFilter(connection);
          if (useCommonDatasourceFilter) {
            config = {
              ...config,
              contentProps: undefined,
              Content: () => <div className="modal-container-body-text">{filterQuery.query}</div>,
            };
          } else {
            config = {
              ...config,
              /* This is now part of the editor toolbar header.
              customButtons: [
                {
                  value: 'Copy',
                  valueClicked: 'Copied!',
                  tooltip: 'Copy query to clipboard',
                  onClick: () => {
                    copyToClipboard(filterQuery.query);
                  },
                },
              ],*/
              contentProps: {
                data: filterQuery.query,
                ref: refContentForm,
                readOnly: true,
              },
              Content: QueryEditor,
            };
          }
          break;
        }
        case 'editCode':
          config = {
            ...config,
            Content: QueryEditor,
          };
          break;
        case 'editImportQuery':
          config = {
            ...config,
            Content: QueryEditor,
          };
          break;
        case 'editRelationshipQuery':
          config = {
            ...config,
            Content: QueryEditor,
          };
          break;
        case 'editLineageQuery':
          config = {
            ...config,
            Content: QueryEditor,
          };
          break;
        case 'showMessage':
          config = {
            ...config,
            Content: () => (
              <div className="modal-container-body-text wide" ref={refContainerBody}>
                <p>{data}</p>
              </div>
            ),
          };
          break;
        case 'editSettings':
          config = {
            ...config,
            Content: () => (
              <div className="modal-container-body-text">
                <p>WARNING!</p>
                <p>
                  Saving settings will reboot the backend API service. Katalogue will be
                  unresponsible for all users until the reboot is finished. This normally takes a
                  couple of seconds.
                </p>
                <p>
                  If the backend server fails to reboot due to corrupt setting values, update those
                  directly in the public.setting table in the Katalogue repository database and
                  reboot the backend service manually.
                </p>
              </div>
            ),
          };
          break;

        case 'generateClientSecret':
          config = {
            ...config,
            Content: () => (
              <div className="modal-container-body-text">
                <p>WARNING!</p>
                <p>
                  This will replace the existing client secret with a new one.
                  <br />
                  The old client secret will be invalidated.
                  <br />
                  Remember to share the new secret with the client owner.
                </p>
              </div>
            ),
          };
          break;

        case 'rotateOidcSigningKey':
          config = {
            ...config,
            Content: () => (
              <div className="modal-container-body-text">
                <p>WARNING!</p>
                <p>
                  This will replace the existing private key used to sign access tokens for the REST
                  API with a new one.
                  <br />
                  All issued access tokens will instantly be invalidated.
                </p>
                <p>
                  This action will reboot the backend API service. Katalogue will be unresponsible
                  for all users until the reboot is finished. This normally takes a couple of
                  seconds.
                </p>
                <p>
                  If the backend server fails to reboot due to corrupt setting values, update those
                  directly in the public.setting table in the Katalogue repository database and
                  reboot the backend service manually.
                </p>
              </div>
            ),
          };
          break;

        case 'addedOidcClient':
          config = {
            ...config,
            Content: () => (
              <div>
                <p>Save the client secret now. It cannot be retrieved later.</p>
                {data.map((x, index) => {
                  return (
                    <p key={index} className="modal-container-body-form">
                      <h4>{x.oidc_client_name}</h4>
                      <div>
                        <span className="label">Client ID</span>
                        <span className="code no-result-text">{x.oidc_client_code}</span>
                      </div>
                      <div>
                        <span className="label">Client Secret</span>
                        <span className="code with-background">{x.oidc_client_secret}</span>
                      </div>
                    </p>
                  );
                })}
              </div>
            ),
          };
          break;
        default:
          break;
      }

      setLoadingModalDialog(false);
      setModalDialog({ ...defaultConfig, ...config });
    },
    []
  );

  return {
    showModalDialog,
    hideModalDialog,
    show,
    loadingModalDialog,
    setLoadingModalDialog,
    modalDialog,
    setModalDialog,
  };
};
